import React, { ReactElement, ReactNode } from 'react';
import { ColumnProps, TableEventListeners } from 'antd/lib/table';
import { getColumnTextSearchProps } from '@app/components/tableFilters/textSearch/byTextSearchFilterCreator';
import { Checkbox, CircularProgress } from '@mui/material';
import {
  BooleanFilterOptions,
  getColumnBooleanFilterProps,
} from '@app/components/tableFilters/singleValue/byBooleanFieldFilterCreator';
import Loadable from '@app/utils/Loadable';
import styled from '@emotion/styled';
import { css, cx } from '@emotion/css';
import TableOfCards from '@app/components/tables/TableOfCards';
import { compare } from '@app/utils/comparatorUtils';

export interface Item {
  id: string;
}

interface Props<T extends Item> {
  id: string;
  dataTestId?: string;
  contentColumnTitle: string;
  readonly?: boolean;
  allItems: Loadable<T[]>;
  activeIds: string[];
  changingIds: string[];
  changedIds?: string[];
  indeterminateIds?: string[];
  itemComparator?: Comparator<T>;
  itemKeywordsGetter?: (item: T) => number | number[] | string | string[] | null | undefined;
  className?: string;
  itemRenderer(item: T): ReactNode;
  onAdd(id: string): Promise<void>;
  onRemove(id: string): Promise<void>;
  disableItem?: (id: string) => boolean;
}

interface TableRow<T> {
  item: T;
  isActive: {
    disabled: boolean;
    loading: boolean;
    value: boolean;
    changed: boolean;
    indeterminate: boolean;
  };
}

export default function CheckboxCardsList<T extends Item>({
  id,
  dataTestId,
  readonly,
  contentColumnTitle,
  allItems,
  activeIds,
  changingIds,
  changedIds = [],
  indeterminateIds = [],
  itemComparator,
  itemRenderer,
  itemKeywordsGetter,
  onAdd,
  onRemove,
  className,
  disableItem,
}: Props<T>): ReactElement {
  function buildTableRows(
    allItems: T[],
    activeIds: string[],
    changingIds: string[],
    readonly: boolean | undefined,
    isRowDisabledFunc: ((id: string) => boolean) | undefined,
  ): TableRow<T>[] {
    return allItems.map((item) => buildTableRow(item, activeIds, changingIds, readonly, isRowDisabledFunc));
  }

  function buildTableRow(
    item: T,
    activeIds: string[],
    changingIds: string[],
    readonly: boolean | undefined,
    isRowDisabledFunc: ((id: string) => boolean) | undefined,
  ): TableRow<T> {
    const isCurrentItemLoading = changingIds.includes(item.id);
    const isCurrentItemDisabled = isRowDisabledFunc?.(item.id) ?? false;

    return {
      item,
      isActive: {
        value: activeIds.includes(item.id),
        loading: isCurrentItemLoading,
        changed: changedIds.includes(item.id),
        indeterminate: indeterminateIds.includes(item.id),
        disabled: readonly || (!isCurrentItemLoading && !!changingIds.length) || isCurrentItemDisabled,
      },
    };
  }

  async function onItemActiveChanged(id: string, newValue: boolean): Promise<void> {
    if (newValue) {
      await onAdd(id);
    } else {
      await onRemove(id);
    }
  }

  const tableRows = buildTableRows(allItems.result ?? [], activeIds, changingIds, readonly, disableItem);

  return (
    <TableOfCards
      id={id}
      dataTestId={dataTestId}
      columns={createColumns(
        tableRows,
        contentColumnTitle,
        itemComparator,
        itemRenderer,
        itemKeywordsGetter,
        onItemActiveChanged,
      )}
      dataSource={tableRows}
      className={className}
      pagination={false}
      loading={!allItems.isResolved()}
      rowKey={(row: TableRow<T>): string => row.item.id}
      rowClickable={!readonly}
      rowClassName={(row: TableRow<T>): string =>
        cx(row.isActive.changed && changedRowClassName, isRowLocked(row) && lockedRowClassName)
      }
      onRow={(row: TableRow<T>): TableEventListeners => ({
        'data-changed': row.isActive.changed,
        onClick: (): void => {
          // Selecting text by dragging also ends in a click
          if (isRowLocked(row) || window.getSelection()?.toString()) {
            return;
          }

          onItemActiveChanged(row.item.id, !row.isActive.value);
        },
      })}
    />
  );
}

function createColumns<T extends Item>(
  data: TableRow<T>[],
  contentColumnTitle: string,
  itemComparator: Comparator<T> | undefined,
  itemRenderer: (item: T) => React.ReactNode,
  itemKeywordsGetter: ((item: T) => number | number[] | string | string[] | null | undefined) | undefined,
  onItemActiveChanged: (id: string, newValue: boolean) => Promise<void>,
): ColumnProps<TableRow<T>>[] {
  return [
    {
      title: <div data-testid='lblActive'>Active</div>,
      dataIndex: 'active',
      key: 'active',
      width: '120px',
      sorter: compare.byField((org) => org.isActive.value, compare.booleans()),
      render: (text: any, record: TableRow<T>): React.ReactNode => (
        <CheckboxContainer>
          {record.isActive.loading ? (
            <CircularProgress color='primary' size={20} data-testid='loader-cbox-item-active' />
          ) : (
            <Checkbox
              id='cbox-item-active'
              data-testid='cbox-item-active'
              checked={record.isActive.value}
              indeterminate={record.isActive.indeterminate}
              disabled={record.isActive.disabled}
              // The row toggles on click too, so a click on the box itself must not reach it
              onClick={(e): void => e.stopPropagation()}
              onChange={(e): void => {
                onItemActiveChanged(record.item.id, e.currentTarget.checked);
              }}
              color='primary'
              sx={record.isActive.changed ? changedCheckboxSx : undefined}
            />
          )}
        </CheckboxContainer>
      ),
      ...getColumnBooleanFilterProps<TableRow<T>>(
        'Is Active',
        (record) => record.isActive.value,
        (booleanFilterOptions: BooleanFilterOptions): string => {
          switch (booleanFilterOptions) {
            case BooleanFilterOptions.all:
              return 'All';
            case BooleanFilterOptions.onlyTrue:
              return 'Active';
            case BooleanFilterOptions.onlyFalse:
              return 'Not Active';
          }
        },
      ),
    },
    {
      title: <div data-testid='lblContent'>{contentColumnTitle}</div>,
      dataIndex: 'content',
      key: 'content',
      defaultSortOrder: 'ascend',
      sorter: (record1, record2): number => {
        return itemComparator?.(record1.item, record2.item) ?? record1.item.id.localeCompare(record2.item.id);
      },
      render: (text: any, record: TableRow<T>): React.ReactNode => itemRenderer(record.item),
      ...getColumnTextSearchProps<TableRow<T>>((record) => {
        return itemKeywordsGetter?.(record.item) ?? record.item.id;
      }),
    },
  ];
}

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

function isRowLocked<T extends Item>(row: TableRow<T>): boolean {
  return row.isActive.disabled || row.isActive.loading;
}

const lockedRowClassName = css`
  &&&,
  &&&:hover,
  &&&:active {
    cursor: default;
    box-shadow: var(--box-shadow-1);
  }
`;

const changedCheckboxSx = { color: 'var(--primary-600)', '&.Mui-checked': { color: 'var(--primary-600)' } };

// Tripled to outrank the card row's white cell backgrounds
const changedRowClassName = css`
  &&& > td {
    background: var(--primary-200-50-a-without-transperanc) !important;
  }

  &&& > td:first-child {
    box-shadow: inset 4px 0 0 0 var(--primary-600);
  }
`;
